import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react'

/**
 * Inline Target editor shared by BOTH scoreboards (task #650): the team
 * Scoreboard page's sticky Target column and the in-meeting Scoreboard
 * section's Target cell.
 *
 * Follows the display/edit toggle the score cells already use — the
 * metric-formatted target at rest (comparison prefix, $/%, commas, K/M), a
 * raw number field once active — but activation is a DOUBLE click, so a
 * stray single click on a dense grid can't put a target into edit mode.
 * Enter and leaving the field commit; Escape cancels and restores.
 *
 * While the field is open its draft string is the only source of truth, so a
 * heartbeat/background prop refresh mid-edit cannot reformat or clobber what
 * is being typed.
 */
export default function InlineTargetCell({
  value,
  display,
  canEdit,
  isSaving,
  onSave,
  ariaLabel,
  error,
  align = 'left',
  className,
  title,
  suffix,
  inputWidth = 90,
}: {
  /** The stored target; the edit field pre-fills with this raw number. */
  value: number | null
  /** Formatted text shown at rest. */
  display: string
  /** False wherever the metric name is not an edit affordance either. */
  canEdit: boolean
  isSaving: boolean
  /**
   * The raw field contents on commit. An empty string means "cleared" and is
   * treated exactly the way the metric settings modal treats a blank target.
   */
  onSave: (raw: string) => void
  ariaLabel: string
  /** Last rejected save, rendered inline where the screen has no banner. */
  error?: string | null
  align?: 'left' | 'right'
  /** Applied to the resting element (truncation/typography per screen). */
  className?: string
  title?: string
  /** Rendered after the value at rest — the meeting grid's +/− marker. */
  suffix?: ReactNode
  inputWidth?: number
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  // Enter/Escape finish the edit and unmount the field; the blur that may
  // follow must not commit a second time (or undo an Escape).
  const finishedRef = useRef(false)

  const rawValue = value !== null && value !== undefined ? String(value) : ''

  const start = () => {
    if (!canEdit) return
    // A double click selects the cell's text; clear it so the highlight
    // doesn't linger behind the field that replaces it.
    if (typeof window !== 'undefined') window.getSelection()?.removeAllRanges()
    finishedRef.current = false
    setDraft(rawValue)
    setEditing(true)
  }

  const commit = () => {
    if (finishedRef.current) return
    finishedRef.current = true
    setEditing(false)
    const next = draft.trim()
    // Nothing typed over: no request, so an accidental double click can't
    // rewrite a target with the value it already had.
    if (next === rawValue) return
    onSave(next)
  }

  const cancel = () => {
    finishedRef.current = true
    setEditing(false)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancel()
    }
  }

  const wrapperStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
    gap: '4px',
    minWidth: 0,
  } as const

  return (
    <div
      style={wrapperStyle}
      // A double click here edits THIS target only — it must not reach the
      // row's own click targets.
      onClick={e => e.stopPropagation()}
      onDoubleClick={e => e.stopPropagation()}
    >
      {editing ? (
        <input
          type="number"
          step="any"
          autoFocus
          value={draft}
          aria-label={ariaLabel}
          onChange={e => setDraft(e.target.value)}
          onFocus={e => e.target.select()}
          onBlur={commit}
          onKeyDown={handleKeyDown}
          style={{
            width: `${inputWidth}px`,
            maxWidth: '100%',
            padding: '4px 6px',
            border: '2px solid #351E1D',
            borderRadius: '6px',
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '14px',
            fontWeight: 600,
            color: '#1B1B1B',
            backgroundColor: '#fff',
            textAlign: align === 'right' ? 'right' : 'left',
            outline: 'none',
          }}
        />
      ) : (
        <>
          <span
            className={className}
            title={title}
            aria-label={canEdit ? ariaLabel : undefined}
            role={canEdit ? 'button' : undefined}
            tabIndex={canEdit ? 0 : undefined}
            onDoubleClick={start}
            // Keyboard equivalent of the double click, so the editor is
            // reachable without a mouse.
            onKeyDown={e => {
              if (!canEdit) return
              if (e.key === 'Enter' || e.key === 'F2') {
                e.preventDefault()
                start()
              }
            }}
            style={canEdit ? { cursor: 'pointer' } : undefined}
          >
            {display}
          </span>
          {suffix}
        </>
      )}
      {isSaving && (
        <span style={{ fontSize: '11px', color: '#1B1B1B', fontWeight: 600 }}>saving...</span>
      )}
      {error && (
        <span role="alert" style={{ fontSize: '11px', color: '#ED1C24', fontWeight: 600 }}>
          {error}
        </span>
      )}
    </div>
  )
}
